import { TextModifierButton } from "@circle-react-shared/uikit/TipTap/Extensions/TextModifiers/TextModifierButton";
import { shouldShowToolbar } from "@circle-react-shared/uikit/TipTap/Extensions/TextModifiers/utilities";
import { useBubbleMenuOptions } from "@circle-react-shared/uikit/TipTap/utilities/useBubbleMenuOptions";
import { useAddTipTapLinkModal } from "@circle-react-uikit/TipTapBlockEditor/AddTipTapLinkModal";
import { useUsageContext } from "@circle-react/providers/UsageContext";
import type { Level } from "@tiptap/extension-heading";
import type { Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";

import { t } from "@/i18n-js/instance";

import { useBlockEditorContext } from "../BlockEditorContext";
import { ALIGNMENT_TYPES } from "../constants";
import { isAlignmentActive } from "../utilities/textAlignment";
import { AiCopilotButton } from "./AiCopilotButton";

import "./styles.module.css";

interface TextModifierOption {
  iconType: string;
  isActiveFn?: () => boolean;
  label: string;
  name: string;
  onClick: () => void;
}

interface TextModifiersMenuProps {
  allowBubbleMenuOverflow?: boolean;
  editor: Editor;
  headingLevels: Level[];
  placement?: "top" | "bottom";
  shouldShowCopilot?: boolean;
  showListsInTextModifierMenu?: boolean;
}

const renderButtonSection = (editor: Editor, options: TextModifierOption[]) => (
  <>
    {options.map(({ isActiveFn, name, ...buttonProps }) => {
      const isActive = isActiveFn?.() ?? editor.isActive(name);

      return (
        <TextModifierButton key={name} isActive={isActive} {...buttonProps} />
      );
    })}
  </>
);

const createAlignmentOptions = (editor: Editor): TextModifierOption[] => [
  {
    name: "align-left",
    iconType: "20-alignment-left",
    label: t("settings.emails.builder.align_left"),
    onClick: () =>
      editor.chain().focus().setTextAlign(ALIGNMENT_TYPES.LEFT).run(),
    isActiveFn: () => isAlignmentActive(editor, ALIGNMENT_TYPES.LEFT),
  },
  {
    name: "align-center",
    iconType: "20-alignment-center",
    label: t("settings.emails.builder.align_center"),
    onClick: () =>
      editor.chain().focus().setTextAlign(ALIGNMENT_TYPES.CENTER).run(),
    isActiveFn: () => isAlignmentActive(editor, ALIGNMENT_TYPES.CENTER),
  },
  {
    name: "align-right",
    iconType: "20-alignment-right",
    label: t("settings.emails.builder.align_right"),
    onClick: () =>
      editor.chain().focus().setTextAlign(ALIGNMENT_TYPES.RIGHT).run(),
    isActiveFn: () => isAlignmentActive(editor, ALIGNMENT_TYPES.RIGHT),
  },
];

export const TextModifiersMenu = ({
  editor,
  shouldShowCopilot = false,
  showListsInTextModifierMenu = false,
  placement = "top",
  headingLevels,
  allowBubbleMenuOverflow = true,
}: TextModifiersMenuProps) => {
  const useAddTipTapLink = useAddTipTapLinkModal();
  const { isAdminUsageContext } = useUsageContext();
  const context = useBlockEditorContext();
  const isWithinEmailEditor = context?.isWithinEmailEditor || false;
  const bubbleMenuOptions = useBubbleMenuOptions(
    placement,
    allowBubbleMenuOverflow,
    editor,
  );

  const formattingOptions: TextModifierOption[] = [
    {
      name: "bold",
      iconType: "20-character-bold",
      label: t("tiptap.bubble_menu.bold"),
      onClick: () => editor.chain().focus().toggleBold().run(),
    },
    {
      name: "italic",
      iconType: "20-character-italic",
      label: t("tiptap.bubble_menu.italic"),
      onClick: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      name: "h1",
      iconType: "16-heading1",
      label: t("tiptap.bubble_menu.h1"),
      onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActiveFn: () => editor.isActive("heading", { level: 1 }),
    },
    {
      name: "h2",
      iconType: "20-character-h2",
      label: t("tiptap.bubble_menu.h2"),
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActiveFn: () => editor.isActive("heading", { level: 2 }),
    },
    {
      name: "h3",
      iconType: "20-character-h3",
      label: t("tiptap.bubble_menu.h3"),
      onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActiveFn: () => editor.isActive("heading", { level: 3 }),
    },
    {
      name: "strike",
      iconType: "20-character-strike",
      label: t("tiptap.bubble_menu.strike"),
      onClick: () => editor.chain().focus().toggleStrike().run(),
    },
    {
      name: "underline",
      iconType: "20-character-underline",
      label: t("tiptap.bubble_menu.underline"),
      onClick: () => editor.chain().focus().toggleUnderline().run(),
    },
    {
      name: "link",
      iconType: "20-character-link",
      label: t("tiptap.bubble_menu.link"),
      onClick: () =>
        useAddTipTapLink.show({
          editor,
          isAdminUsageContext,
          isEmailEditor: isWithinEmailEditor,
        }),
    },
    {
      name: "bullet_list",
      iconType: "16-editor-ul",
      label: t("tiptap.bubble_menu.bullet_list"),
      onClick: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      name: "ordered_list",
      iconType: "16-editor-ol",
      label: t("tiptap.bubble_menu.ordered_list"),
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
    },
  ].filter(command => {
    if (command.name === "h1" && !headingLevels.includes(1)) return false;
    if (command.name === "h2" && !headingLevels.includes(2)) return false;
    if (command.name === "h3" && !headingLevels.includes(3)) return false;
    if (command.name === "bullet_list" && !showListsInTextModifierMenu)
      return false;
    return !(command.name === "ordered_list" && !showListsInTextModifierMenu);
  });

  const alignmentOptions = createAlignmentOptions(editor);

  return (
    <BubbleMenu
      editor={editor}
      {...bubbleMenuOptions}
      className="z-[9999]"
      shouldShow={shouldShowToolbar}
    >
      <div className="bg-primary border-primary divide-primary flex gap-1 divide-x rounded-md border p-1 shadow-sm">
        {shouldShowCopilot && <AiCopilotButton editor={editor} />}
        <div className={shouldShowCopilot ? "pl-1" : undefined}>
          {renderButtonSection(editor, formattingOptions)}
        </div>
        {isWithinEmailEditor && (
          <div className="flex items-center justify-center pl-2">
            {renderButtonSection(editor, alignmentOptions)}
          </div>
        )}
      </div>
    </BubbleMenu>
  );
};
